import onChange from 'on-change';
import View from './View';
import Services from '../../services/Services';
import Addresses from '../../services/Addresses';
import ServiceTimes from '../../services/ServiceTimes';
import OnlineServiceRegistration from '../../services/OnlineServiceRegistration';

const MODAL_SELECTOR = '.modal';
const MODAL_CONTENT_SELECTOR = '.modal__body';
const SERVICE_CARD_SELECTOR = '[data-rm-service]';
const ADDRESS_ITEM_SELECTOR = '[data-rm-address]';
const MAX_STEPS = 4;
document.addEventListener('DOMContentLoaded', () => {
	const button = document.querySelectorAll('.modal__btn--next')[3];
	button.setAttribute('disabled', 'disabled');
});
export default () => {
	const state = {
		isOpened: false,
		step: 1,
		services: [],
		address: {
			location: null,
			coords: null,
		},
		allAddressesCoords: null,
		date: new Date(),
		time: null,
		allTimes: null,
		contactDetails: {
			name: null,
			phone: null,
			comment: null
		},
		response: {
			isSuccess: null,
			message: null,
		},

		ui: {
			allServices: null,
			isSpinnerVisible: false,
			isNextBtnHidden: false,
			isPrevBtnHidden: false,
		},

		get addressId() {
			if (!this.hasSelectedAddress) {
				return null;
			}

			if (this.allAddressesCoords === null) {
				return null;
			}
			return this.allAddressesCoords.find(
				(address) => address.location === this.address.location
			).id;
		},

		get isLastStep() {
			return this.step === MAX_STEPS;
		},

		get hasSelectedServices()
		{
			return this.services.length !== 0;
		},

		get hasSelectedAddress() {
			return this.address.location !== null && this.address.coords !== null;
		},

		get hasSelectedDate() {
			return this.date !== null && this.time !== null;
		},

		get isPhoneValid() {
			const isEmpty = this.contactDetails.phone === null;

			if (isEmpty) {
				return false;
			}

			const hasAllNumbers =
				this.contactDetails.phone
					.split('')
					.filter((letter) => !Number.isNaN(parseInt(letter, 10))).length ===
				11;
			return hasAllNumbers;
		},

		get isContactDetailsValid() {
			return (this.contactDetails.name !== null && this.isPhoneValid);
		},

		get isAddressStepAvailable() {
			return this.hasSelectedServices;
		},

		get isDateStepAvailable() {
			return this.isAddressStepAvailable && this.hasSelectedAddress;
		},

		get isContactDetailsStepAvailable() {
			return this.isDateStepAvailable && this.hasSelectedDate;
		},

		get canSendRequest() {
			return this.isContactDetailsStepAvailable && this.isContactDetailsValid;
		},

		get maxAvailableStep() {
			let step = 1;

			if (this.isAddressStepAvailable) {
				step = 2;
			}

			if (this.isDateStepAvailable) {
				step = 3;
			}

			if (this.isContactDetailsStepAvailable) {
				step = 4;
			}

			if (this.canSendRequest) {
				step = 5;
			}

			return step;
		},

		get isServicesNotLoaded() {
			return this.ui.allServices === null;
		},

		get isAddressesNotLoaded() {
			return this.allAddressesCoords === null;
		},

		get isTimesNotLoaded() {
			return this.allTimes === null;
		},
	};

	const modalEl = document.querySelector(MODAL_SELECTOR);
	let disableStepsCheck = false;

	const modalView = new View();
	const watchedState = modalView.init({ modalEl, state });

	const fetchServices = () => {
		watchedState.ui.isSpinnerVisible = true;
		Services.fetch().then((services) => {
			watchedState.ui.allServices = services;
			watchedState.ui.isSpinnerVisible = false;
		});
	};

	const fetchAddresses = () => {
		watchedState.ui.isSpinnerVisible = true;
		Addresses.fetch().then((addresses) => {
			watchedState.allAddressesCoords = addresses.map((address) => ({
				...address,
				location: address.address,
			}));
			watchedState.ui.isSpinnerVisible = false;
		});
	};

	const fetchTimes = () => {
		const selectedServices = watchedState.ui.allServices.filter(({ id }) =>
			watchedState.services.includes(id)
		);
		const servicesTime = selectedServices
			.map(({ duration }) => Number(duration))
			.reduce((acc, value) => acc + value, 0);
		return ServiceTimes.fetch({
			addressId: watchedState.addressId,
			day: watchedState.date,
			duration: servicesTime,
		}).then((times) => {
			onChange.target(watchedState).allTimes = null; // remove me
			watchedState.allTimes = times;
			watchedState.ui.isSpinnerVisible = false;
		});
	};

	const sendRegistrationRequest = ({ contactDetails, services }) => {
		const { date } = watchedState;
		const [hours, minutes] = watchedState.time.split(':');
		date.setHours(hours, minutes, 0);

		const payload = new FormData();

		payload.append('name', contactDetails.name);
		payload.append('phone', contactDetails.phone);
		payload.append('comment', contactDetails.comment);
		payload.append('time', date);
		payload.append('services', services);
		payload.append('address', watchedState.addressId);

		return OnlineServiceRegistration.send(payload);
	};

	const handleMapPlacemarkClick = (targetPlacemark) => {
		watchedState.address = {
			location: targetPlacemark.location,
			coords: targetPlacemark.coord ? targetPlacemark.coord : targetPlacemark.coords,
		};
	};

	const open = () =>
	{
		watchedState.isOpened = true;
		if (watchedState.isServicesNotLoaded)
			fetchServices();
	};

	const handleToggleElsClick = () => {
		open();
	};

	const handleCloseBtnClick = () => {
		watchedState.isOpened = false;
	};

	const handleBodyClick = (ev) => {
		const isClickedAtModalBackdrop =
			ev.target.closest(MODAL_SELECTOR) &&
			!ev.target.closest(MODAL_CONTENT_SELECTOR);

		if (isClickedAtModalBackdrop) {
			watchedState.isOpened = false;
		}
	};

	const handleNextStepBtnClick = () => {
		const prevStep = watchedState.step;
		if (watchedState.step < MAX_STEPS) {
			watchedState.step += 1;
		}

		const isAddressesStep = watchedState.step === 2;
		const isDateStep = watchedState.step === 3;
		const isContactsStep = prevStep === 4;

		if (isAddressesStep && watchedState.isAddressesNotLoaded) {
			fetchAddresses();
		}

		if (isDateStep) {
			const today = new Date();
			fetchTimes().then(() => {
				watchedState.date = today;
			});
		}

		if (isContactsStep && watchedState.canSendRequest) {
			watchedState.ui.isNextBtnHidden = true;
			watchedState.ui.isPrevBtnHidden = true;
			watchedState.ui.isSpinnerVisible = true;
			sendRegistrationRequest(watchedState)
				.then(({ data }) => {
					watchedState.response = {
						isSuccess: true,
						message: data.message,
					};
				})
				.catch((data) => {
					watchedState.response = {
						isSuccess: false,
						message: data.message,
					};
					watchedState.ui.isPrevBtnHidden = false;
				})
				.finally(() => {
					watchedState.step += 1;
					watchedState.ui.isSpinnerVisible = false;
				});
		}
	};

	const handlePrevStepBtnClick = () => {
		const currentStep = watchedState.step;
		if (currentStep > 1) {
			watchedState.step -= 1;
		}
		if (currentStep === 3) {
			const today = new Date();
			watchedState.date = today;
		}
	};

	const handleServiceCardCheckboxChange = (ev) => {
		const cardEl = ev.target.closest(SERVICE_CARD_SELECTOR);
		const targetService = cardEl.dataset.rmService;
		const targetId = Number(targetService);

		const newServices = [...watchedState.services];
		const index = newServices.indexOf(targetService);

		if (index !== -1) {
			newServices.splice(index, 1);
		}

		if (![34, 35, 36].includes(targetId) && index === -1) {
			newServices.push(targetService);
		}

		watchedState.services = newServices;
	};

	const handleAddressClick = (ev) => {
		const addressEl = ev.target.closest(ADDRESS_ITEM_SELECTOR);
		const targetAddress = addressEl.dataset.rmAddress;

		const targetAddressCoords = JSON.parse(addressEl.dataset.rmAddressCoords);

		watchedState.address = {
			location: targetAddress,
			coords: targetAddressCoords,
		};
	};

	const handleDateChange = ([selectedDate]) => {
		watchedState.date = selectedDate;
		watchedState.time = null;
		fetchTimes();
	};

	const handleTimeClick = (ev) => {
		const timeEl = ev.target.closest('[data-rm-time]');
		const timeElInput = timeEl?.querySelector('.modal__time-input');
		if (timeElInput?.disabled) {
			return;
		}
		const isTimeNotSelected = timeEl === null;
		if (isTimeNotSelected) {
			return;
		}

		watchedState.time = timeEl.dataset.rmTime;
	};

	const handleNameInput = (ev) => {
		const { value } = ev.target;
		const button = document.querySelectorAll('.modal__btn--next')[3];
		if (!(watchedState.contactDetails.phone && watchedState.contactDetails.name))
			button.setAttribute('disabled', 'disabled');

		if (value.trim().length === 0) {
			watchedState.contactDetails.name = null;
			button.setAttribute('disabled', 'disabled');
			return;
		}
		watchedState.contactDetails.name = value;
		if (watchedState.contactDetails.phone && watchedState.contactDetails.name)
			button.removeAttribute('disabled');
		else button.setAttribute('disabled', 'disabled');
	};

	const handlePhoneInput = (ev) => {
		const { value } = ev.target;
		let countNumber = 0;
		const button = document.querySelectorAll('.modal__btn--next')[3];
		if (!(watchedState.contactDetails.phone && watchedState.contactDetails.name))
			button.setAttribute('disabled', 'disabled');
		for (let i = 0; i < value.length; i++) {
			if (!isNaN(parseInt(value[i], 10))) countNumber += 1;
		}
		if (value.length === 0 || countNumber < 11) {
			watchedState.contactDetails.phone = null;
			button.setAttribute('disabled', 'disabled');
			return;
		}
		watchedState.contactDetails.phone = value;
		if (watchedState.contactDetails.phone && watchedState.contactDetails.name)
			button.removeAttribute('disabled');
		else button.setAttribute('disabled', 'disabled');
	};

	const handleCommentInput = (ev) => {
		const { value } = ev.target;
		const button = document.querySelectorAll('.modal__btn--next')[3];
		if (!(watchedState.contactDetails.phone && watchedState.contactDetails.name))
			button.setAttribute('disabled', 'disabled');
		if (value.length === 0) {
			watchedState.contactDetails.comment = null;
			return;
		}
		watchedState.contactDetails.comment = value;
		if (watchedState.contactDetails.phone && watchedState.contactDetails.name)
			button.removeAttribute('disabled');
		else button.setAttribute('disabled', 'disabled');
	};

	const handleAgreeChange = (ev) => {
		const { checked } = ev.target;
		const button = document.querySelectorAll('.modal__btn--next')[3];
		if (!(watchedState.contactDetails.phone && watchedState.contactDetails.name))
			button.setAttribute('disabled', 'disabled');
		if (watchedState.contactDetails.phone && watchedState.contactDetails.name)
			button.removeAttribute('disabled');
		else button.setAttribute('disabled', 'disabled');
	};

	const setComment = (text) => {
		watchedState.contactDetails.comment = text;
	};

	const openStep = (number) =>
	{
		if (number < 1)
		{
			watchedState.step = 1;
			return;
		}

		if (disableStepsCheck)
		{
			watchedState.step = number;
			return;
		}

		if (number > watchedState.maxAvailableStep)
		{
			watchedState.step = watchedState.maxAvailableStep;
			return;
		}
		watchedState.step = number;
	};

	const resetChosenServices = () =>
	{
		console.log('reset');
		watchedState.services = [];
	};

	const appendService = (service) => {
		if ([34, 35, 36].includes(Number(service.id))) return;
		const checkbox = document.querySelector("[data-rm-service='" + service.id + "']").children[0].children[0].children[0];
		if (!checkbox) return;

		if (!checkbox.checked) {
			checkbox.checked = true;
			checkbox.dispatchEvent(new Event('change', { bubbles: true }));
			// console.log('checkbox set via change', service.id);
		} else {
			// console.log('checkbox already set', service.id);
		}
	};

	const setStepsCheckDisabled = (value) =>
	{
		disableStepsCheck = value;
	}

	modalView.initListeners({
		handleToggleElsClick,
		handleCloseBtnClick,
		handleBodyClick,
		handleNextStepBtnClick,
		handlePrevStepBtnClick,
		handleServiceCardCheckboxChange,
		handleMapPlacemarkClick,
		handleAddressClick,
		handleDateChange,
		handleTimeClick,
		handleNameInput,
		handlePhoneInput,
		handleCommentInput,
		handleAgreeChange,
	});

	modalView.render({ step: watchedState.step });

	return {
		open,
		setComment,
		openStep,
		resetChosenServices,
		appendService,
		setStepsCheckDisabled,
		fetchAddresses
	};
};
